import { X } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/ux-common/components/button/button';
import { cn } from '@/ux-common/lib/utils';

/**
 * Alert — an inline alert: a dark tinted bar with a severity-coloured border, a
 * leading glyph, a one-line message and a dismiss X. It sits in whatever contains
 * it — a Stack, a Card, a form section — and takes that container's width. For a
 * notice that pushes the whole app down, use Banner.
 *
 * All visual styling is applied via CSS classes. Severity is a class on the root,
 * not a prop: `.alert-good`, `.alert-caution`, `.alert-warning`, `.alert-bad`.
 * A bare `.alert` renders `good`, so always compose one.
 *
 * The body is a single line that truncates. Put the full message in the root's
 * `title` so it stays reachable; compose `.alert-wrap` to let the body wrap and the
 * alert grow instead.
 *
 * Slots ship as thin wrappers: `AlertIcon` (the consumer passes the glyph — CSS
 * colours it), `AlertBody`, `AlertTitle`, `AlertDescription` and `AlertDismiss` (a
 * registry Button — wire `onClick`).
 *
 * @example
 * ```tsx
 * <Alert className="alert-bad" title="Connection lost: Reconnecting to the facility.">
 *   <AlertIcon><XCircle /></AlertIcon>
 *   <AlertBody>
 *     <AlertTitle>Connection lost:</AlertTitle>{' '}
 *     <AlertDescription>Reconnecting to the facility.</AlertDescription>
 *   </AlertBody>
 *   <AlertDismiss onClick={onDismiss} />
 * </Alert>
 * ```
 */
export type AlertProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * The root defaults to `role="status"`, a polite live region. Pass `role="alert"`
 * for a notice that must interrupt — typically `alert-bad` raised in response to
 * something the user just did.
 */
export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="status"
        data-component="Alert"
        className={cn('alert', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Alert.displayName = 'Alert';

/**
 * AlertIcon — the leading glyph slot. Renders `<span class="alert-icon">`, hidden
 * from assistive tech; the severity is carried by the message, not the glyph.
 */
export type AlertIconProps = React.HTMLAttributes<HTMLSpanElement>;

export const AlertIcon = React.forwardRef<HTMLSpanElement, AlertIconProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        aria-hidden="true"
        data-component="AlertIcon"
        className={cn('alert-icon', className)}
        {...props}
      >
        {children}
      </span>
    );
  }
);

AlertIcon.displayName = 'AlertIcon';

/** AlertBody — the message line. Renders `<p class="alert-body">`; holds the title and description. */
export type AlertBodyProps = React.HTMLAttributes<HTMLParagraphElement>;

export const AlertBody = React.forwardRef<HTMLParagraphElement, AlertBodyProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <p ref={ref} data-component="AlertBody" className={cn('alert-body', className)} {...props}>
        {children}
      </p>
    );
  }
);

AlertBody.displayName = 'AlertBody';

/** AlertTitle — the bold uppercase lead-in. Renders `<strong class="alert-title">`. */
export type AlertTitleProps = React.HTMLAttributes<HTMLElement>;

export const AlertTitle = React.forwardRef<HTMLElement, AlertTitleProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <strong
        ref={ref}
        data-component="AlertTitle"
        className={cn('alert-title', className)}
        {...props}
      >
        {children}
      </strong>
    );
  }
);

AlertTitle.displayName = 'AlertTitle';

/** AlertDescription — the message text after the title. Renders `<span class="alert-description">`. */
export type AlertDescriptionProps = React.HTMLAttributes<HTMLSpanElement>;

export const AlertDescription = React.forwardRef<HTMLSpanElement, AlertDescriptionProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        data-component="AlertDescription"
        className={cn('alert-description', className)}
        {...props}
      >
        {children}
      </span>
    );
  }
);

AlertDescription.displayName = 'AlertDescription';

/**
 * AlertDismiss — the trailing X. Composes a registry Button; wire `onClick` and
 * unmount the Alert yourself — this component holds no open state.
 *
 * `type` is omitted and pinned to `"button"` so an alert rendered inside a form
 * cannot submit it. `aria-label` defaults to "Dismiss" and stays overridable.
 */
export type AlertDismissProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>;

export const AlertDismiss = React.forwardRef<HTMLButtonElement, AlertDismissProps>(
  ({ className, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        aria-label="Dismiss"
        data-component="AlertDismiss"
        className={cn('alert-dismiss', className)}
        {...props}
        type="button"
      >
        <X className="btn-icon" aria-hidden="true" />
      </Button>
    );
  }
);

AlertDismiss.displayName = 'AlertDismiss';
